import { useEffect } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAxios, setCurrentAccountUniqueIdentifierInLocalStorage } from '../../core/_axios';
import usePageTitle from '../../hooks/usePageTitle';
import ManageAccountForm from './ManageAccountForm';
import { UsersList } from '../../components/UsersList';
import ManageAccountUserForm from './ManageAccountUserForm';
import AccountBillingForm from './AccountBillingForm';
import useAccount from '../../hooks/useAccount';
import { useAuth } from '../../AuthContext';
import AccountAdminForm from './AccountAdminForm';
import AccountEmailSettings from './AccountEmailSettings';
import useAccountThatIsHydrated from '../../hooks/useAccountThatIsHydrated';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';
import { usePageHeader } from '../../contexts/PageHeaderContext';

export function ManageSpecificAccountRoutes({
    isForCurrentAccount = false,
}: {
    isForCurrentAccount?: boolean;
}) {
    const {
        currentUser,
    } = useAuth();

    const axios = useAxios();
    const { setPageHeader } = usePageHeader();

    const {
        pathname,
        search
    } = useLocation();

    // Set by the add-account form right after it creates an account and makes it the working one.
    const justCreated = new URLSearchParams(search).get('created') === '1';

    const params = useParams();

    const { copy } = useLocalizedCopy();

    usePageTitle(copy.Account.details.title);

    const {
        loading,
        loadingError,
        account,
        setAccount,
        onLoadAccount: loadAccount,
    } = useAccountThatIsHydrated({
        axios,
        isForCurrentAccount,
        accountUniqueId: isForCurrentAccount ? null : params.accountUniqueId || null
    });

    const {
        account: currentAccount
    } = useAccount({
        axios,
        isForCurrentAccount: true,
    });

    // Set up page header
    useEffect(() => {
        if (account) {
            setPageHeader(`${copy.Account.details.title}: ${account.name}`);
        }
        return () => setPageHeader(null);
    }, [setPageHeader, account, copy.Account.details.title]);

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadAccount}>
                        {copy.Common.messages.tryAgain}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fixed">
            {justCreated && (
                <div role="status" className="mb-4 bg-green-50 border border-green-300 text-green-800 px-4 py-3 rounded">
                    Account created. You are now working in <strong>{account.name}</strong>: the Services, Signatures and iPads pages show this account.
                </div>
            )}
            {/* Switch Account button for admins */}
            {(currentUser.is_agency_admin || currentUser.is_super_super_user) && !isForCurrentAccount && (account.unique_identifier !== currentAccount?.unique_identifier) && (
                <div className="mb-4">
                    <button
                        className="btn btn-sm btn-light"
                        onClick={() => {
                            // Set the selected account in local storage
                            setCurrentAccountUniqueIdentifierInLocalStorage(account.unique_identifier);
                            // Reload the page to switch to the selected account
                            window.location.reload();
                        }}
                    >
                        {copy.Common.labels.select} {copy.Common.navigation.account}
                    </button>
                </div>
            )}
            <div className='mb-5'>
                <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:border-gray-700 dark:text-gray-400">
                    <li className="me-2">
                        <Link
                            to={
                                isForCurrentAccount
                                    ? `/manage-account/details`
                                    : `/management/accounts/${account.unique_identifier}/details`
                            }
                            className={[
                                (isForCurrentAccount
                                    ? pathname === `/manage-account/details`
                                    : pathname === `/management/accounts/${account.unique_identifier}/details`)
                                    ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                    : 'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}
                        >
                            {copy.Account.details.title}
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link
                            to={
                                isForCurrentAccount
                                    ? `/manage-account/users`
                                    : `/management/accounts/${account.unique_identifier}/users`
                            }
                            className={[
                                (isForCurrentAccount
                                    ? pathname === `/manage-account/users`
                                    : pathname === `/management/accounts/${account.unique_identifier}/users`)
                                    ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                    : 'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}
                        >
                            {copy.Account.users.title}
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link
                            to={
                                isForCurrentAccount
                                    ? `/manage-account/add-user`
                                    : `/management/accounts/${account.unique_identifier}/add-user`
                            }
                            className={[
                                (isForCurrentAccount
                                    ? pathname === `/manage-account/add-user`
                                    : pathname === `/management/accounts/${account.unique_identifier}/add-user`)
                                    ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                    : 'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}
                        >
                            {copy.Account.addUser.title}
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link
                            to={
                                isForCurrentAccount
                                    ? `/manage-account/billing`
                                    : `/management/accounts/${account.unique_identifier}/billing`
                            }
                            className={[
                                (isForCurrentAccount
                                    ? pathname.startsWith(`/manage-account/billing`)
                                    : pathname.startsWith(`/management/accounts/${account.unique_identifier}/billing`))
                                    ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                    : 'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}
                        >
                            {copy.Account.billing.title}
                        </Link>
                    </li>
                    <li className="me-2">
                        <Link
                            to={
                                isForCurrentAccount
                                    ? `/manage-account/email-settings`
                                    : `/management/accounts/${account.unique_identifier}/email-settings`
                            }
                            className={[
                                (isForCurrentAccount
                                    ? pathname === `/manage-account/email-settings`
                                    : pathname === `/management/accounts/${account.unique_identifier}/email-settings`)
                                    ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                    : 'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}
                        >
                            Email Settings
                        </Link>
                    </li>
                    {currentUser.is_super_super_user && (
                        <li className="me-2">
                            <Link
                                to={
                                    isForCurrentAccount
                                        ? `/manage-account/admin`
                                        : `/management/accounts/${account.unique_identifier}/admin`
                                }
                                className={[
                                    (isForCurrentAccount
                                        ? pathname === `/manage-account/admin`
                                        : pathname === `/management/accounts/${account.unique_identifier}/admin`)
                                        ? 'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500'
                                        : 'text-gray-500 dark:text-gray-400',
                                    'inline-block p-4'
                                ].join(' ')}
                            >
                                {copy.Admin.title}
                            </Link>
                        </li>
                    )}
                </ul>
            </div>
            <Routes>
                <Route path="/details" element={
                    <ManageAccountForm
                        account={account}
                        setAccount={setAccount}
                        isForCurrentAccount={isForCurrentAccount}
                    />}
                />
                <Route path="/users" element={
                    <UsersList
                        account={account}
                        isForCurrentAccount={isForCurrentAccount}
                    />}
                />
                <Route path="/add-user" element={
                    <ManageAccountUserForm
                        account={account}
                        isForCurrentAccount={isForCurrentAccount}
                    />}
                />
                <Route path="/billing/*" element={
                    <AccountBillingForm
                        accountUniqueIdentifier={isForCurrentAccount ? null : account.unique_identifier}
                        isForCurrentAccount={isForCurrentAccount}
                    />}
                />
                <Route path="/email-settings" element={
                    <AccountEmailSettings
                        account={account}
                        setAccount={setAccount}
                        isForCurrentAccount={isForCurrentAccount}
                    />}
                />
                <Route path="/admin" element={
                    <AccountAdminForm
                        account={account}
                        setAccount={setAccount}
                    />}
                />
                {/* Redirect to details page if no match */}
                {isForCurrentAccount ? <Route
                    path="*"
                    element={<Navigate
                        to={`/manage-account/details`}
                        replace
                    />}
                /> : <Route
                    path="*"
                    element={<Navigate
                        to={`/management/accounts/${account.unique_identifier}/details`}
                        replace
                    />}
                />}
            </Routes>
        </div>
    );
}